import React, { useState } from 'react';
import { Flame, ChevronRight, ChevronLeft, Sparkles, Trophy, Target, BookOpen } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * ForgeOnboarding - Introduction tutorial for new Forge Path users
 */

const ONBOARDING_SLIDES = [
  {
    id: 1,
    title: "Welcome to the Forge Path",
    icon: Flame,
    iconColor: "text-orange-500",
    content: `Welcome, aspiring language smith! You are about to embark on an extraordinary journey through the ancient art of word forging. The Forge Path is a transformative experience designed to help you master the 500 most essential words in your target language through the timeless craft of the blacksmith.

In the medieval forges of old, master smiths would transform raw iron into magnificent weapons and tools through patience, repetition, and dedication. Similarly, you will transform unfamiliar words into powerful tools of communication.

Your journey begins at the entrance of the forge, where a small flame burns with potential. As you progress through each lesson, you will ascend the ancient stone path toward the grand castle that awaits at the summit.`,
  },
  {
    id: 2,
    title: "The Four Levels of Mastery",
    icon: Trophy,
    iconColor: "text-yellow-500",
    content: `Every lesson consists of four distinct levels of mastery, each named after the metals that blacksmiths work with:

**Iron Level** — Your first encounter with new vocabulary. Questions focus on basic recognition and simple translations. Like iron fresh from the mine, these words are rough but full of potential.

**Bronze Level** — The words begin to take shape. More challenging questions test your understanding in context. Sentences help you see how words function in daily conversation.

**Silver Level** — Your knowledge is refined and polished. Questions require you to translate from the target language, proving true understanding rather than pattern recognition.

**Gold Level** — The pinnacle of mastery. You demonstrate complete command through typing exercises, sentence construction, and speaking challenges. Words forged to gold will serve you for a lifetime.`,
  },
  {
    id: 3,
    title: "Your Blacksmith Companion",
    icon: Sparkles,
    iconColor: "text-amber-400",
    content: `Throughout your journey, you will be accompanied by a blacksmith character who represents your growing expertise. This companion evolves through ten distinct ranks as you complete lessons at the Gold level:

**Spark Novice** — You have just lit your first forge fire
**Ember Apprentice** — The flames of learning grow stronger
**Flame Journeyman** — You can maintain a steady heat
**Blaze Artisan** — Your skills are becoming refined
**Inferno Craftsman** — The forge responds to your will
**Molten Expert** — You work with liquid metal confidence
**Radiant Master** — Your reputation spreads far and wide
**Solar Grandmaster** — Few can match your dedication
**Stellar Legend** — Your name is spoken with reverence
**Cosmic Forgefather** — You have achieved ultimate mastery`,
  },
  {
    id: 4,
    title: "Daily Practice & Spaced Repetition",
    icon: Target,
    iconColor: "text-green-500",
    content: `The Forge Path incorporates proven learning science to ensure your vocabulary sticks permanently:

**Spaced Repetition** — Words you struggle with appear more frequently. The system tracks your accuracy and schedules review sessions at optimal intervals.

**Word Strength Decay** — Just as a forge cools without attention, your word knowledge can fade over time. Gold-mastered words will gradually lose their shine if not reviewed. Visual indicators show which lessons need attention.

**The Reforge** — Lesson 25 of each course focuses exclusively on your weakest words. This milestone lesson ensures no word is left behind.

**Daily Sentences** — Each lesson includes practical, everyday sentences demonstrating how vocabulary is used in real conversations. You're not just learning words — you're learning to communicate.`,
  },
  {
    id: 5,
    title: "The Forge Stories",
    icon: BookOpen,
    iconColor: "text-indigo-400",
    content: `Alongside the Forge Path, a serialized graded reader follows four friends — **Mia**, **Leo**, **Nina**, and **Kai** — through an unfolding mystery across 100 episodes.

Each episode is vocabulary-locked to its corresponding Forge lesson, so you read a story built from the exact words you're learning. A grandmother's hidden method, a lost manuscript, a journey across cities and mountains — the narrative pulls you forward while reinforcing every word through natural context.

**Course 1** — Mia arrives in a new city. Meets Leo, reunites with Nina. A mysterious letter from her grandmother sets everything in motion.
**Course 2** — Daily life deepens. A second manuscript is discovered. The grandmother's life work begins to emerge.
**Course 3** — Emotions, society, and a national debate. The method goes public. Rosa, the grandmother's partner, appears.
**Course 4** — Professional mastery. Global impact. The full circle — the method proves itself through the story of those who lived it.

**Every word you encounter in the story counts as exposure** — the same SRS tracking, the same color-coding, the same popovers.`,
  },
  {
    id: 6,
    title: "Begin Your Journey",
    icon: Flame,
    iconColor: "text-orange-500",
    content: `You now understand the path that lies before you. The forge is lit, the anvil awaits, and your blacksmith companion stands ready:

**Course 1: Foundations** — Words 1-500, the essential building blocks
**Course 2: Builder** — Words 501-1000, expanding your vocabulary
**Course 3: Artisan** — Words 1001-1500, refining your expression
**Course 4: Master** — Words 1501-2000, achieving fluency

Each course contains 25 lessons organized by theme: greetings, family, numbers, food, travel, work, and more. Complete all lessons at Gold level to unlock the next course.

Remember: every master blacksmith began as an apprentice. The path may seem long, but each step brings you closer to your goal.

**Are you ready to begin? The forge awaits!**`,
  },
];

export default function ForgeOnboarding({ onComplete, onSkip, languageCode }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slide = ONBOARDING_SLIDES[currentSlide];
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === ONBOARDING_SLIDES.length - 1;
  const isStorySlide = slide.id === 5;
  const Icon = slide.icon;

  const handleNext = () => {
    if (isLastSlide) {
      onComplete?.();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstSlide) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleGoToStories = () => {
    onComplete?.();
    navigate(`/forge-stories/${languageCode || 'es'}?course=1`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(251, 146, 60, 0.15) 0%, transparent 70%)' }}
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 70%)' }}
        />
      </div>

      {/* Main modal */}
      <div 
        className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
          border: '2px solid rgba(251, 146, 60, 0.3)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(251, 146, 60, 0.1)'
        }}
      >
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-amber-900/30">
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 text-sm text-slate-500 hover:text-slate-300 transition-colors"
          >
            Skip intro
          </button>

          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                border: '1px solid rgba(251, 146, 60, 0.3)'
              }}
            >
              <Icon className={`h-7 w-7 ${slide.iconColor}`} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-amber-100">
                {slide.title}
              </h2>
              <p className="text-sm text-slate-500">
                Step {currentSlide + 1} of {ONBOARDING_SLIDES.length}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <div className="prose prose-invert prose-amber max-w-none" style={{ lineHeight: '1.8', color: '#cbd5e1' }}>
            {slide.content.split('\n\n').map((paragraph, idx) => (
              <p 
                key={idx} 
                className="mb-4 text-slate-300"
                dangerouslySetInnerHTML={{
                  __html: paragraph
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-amber-300 font-semibold">$1</strong>')
                    .replace(/\n/g, '<br/>')
                }}
              />
            ))}
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 py-4">
          {ONBOARDING_SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                idx === currentSlide 
                  ? 'bg-amber-500 w-8' 
                  : idx < currentSlide 
                    ? 'bg-amber-600/50' 
                    : 'bg-slate-600'
              }`}
            />
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between p-6 pt-2 border-t border-slate-800/50">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={isFirstSlide}
            className={`text-slate-400 hover:text-white ${isFirstSlide ? 'invisible' : ''}`}
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {/* Story shortcut on the story slide */}
            {isStorySlide && (
              <Button
                variant="outline"
                onClick={handleGoToStories}
                className="gap-2 border-indigo-500/40 text-indigo-300 hover:bg-indigo-950/40"
                data-testid="go-to-stories-btn"
              >
                <BookOpen className="h-4 w-4" />
                Read the Stories
              </Button>
            )}

            <Button
              onClick={handleNext}
              className="px-8 py-3 font-semibold"
              style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#1c1917'
              }}
            >
              {isLastSlide ? (
                <>
                  <Flame className="h-5 w-5 mr-2" />
                  Enter the Forge
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="h-5 w-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
